<template>
  <Teleport to="body">
    <Transition name="loader-fade">
      <div
        v-if="isVisible"
        class="modern-loader-overlay"
        role="dialog"
        aria-label="Loading"
      >
        <div class="modern-loader-container">
          <div
            class="modern-spinner"
            :class="{ 'spinner-small': size === 'small' }"
          >
            <div class="spinner-ring"></div>
            <div class="spinner-ring"></div>
            <div class="spinner-ring"></div>
          </div>
          <p v-if="message" class="loader-message">{{ message }}</p>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
export default {
  name: "ModernLoader",
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    message: {
      type: String,
      default: "Loading...",
    },
    size: {
      type: String,
      default: "normal",
      validator: (value) => ["small", "normal", "large"].includes(value),
    },
    delay: {
      type: Number,
      default: 100,
    },
  },
  data() {
    return {
      isVisible: false,
      timer: null,
    };
  },
  watch: {
    loading: {
      handler(newVal) {
        if (newVal) {
          // Add a small delay to prevent flashing for very quick operations
          this.timer = setTimeout(() => {
            this.isVisible = true;
          }, this.delay);
        } else {
          // Clear timer if loading is set to false before delay
          if (this.timer) {
            clearTimeout(this.timer);
            this.timer = null;
          }
          this.isVisible = false;
        }
      },
      immediate: true,
    },
  },
  beforeUnmount() {
    if (this.timer) {
      clearTimeout(this.timer);
    }
  },
};
</script>

<style scoped>
.modern-loader-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  font-family:
    -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}

.modern-loader-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.modern-spinner {
  position: relative;
  width: 60px;
  height: 60px;
}

.modern-spinner.spinner-small {
  width: 40px;
  height: 40px;
}

.spinner-ring {
  position: absolute;
  width: 100%;
  height: 100%;
  border: 3px solid transparent;
  border-radius: 50%;
  animation: spin 1.5s linear infinite;
}

.spinner-ring:nth-child(1) {
  border-top-color: #007bff;
  animation-delay: 0s;
}

.spinner-ring:nth-child(2) {
  border-right-color: #007bff;
  animation-delay: 0.2s;
  transform: scale(0.8);
}

.spinner-ring:nth-child(3) {
  border-bottom-color: #007bff;
  animation-delay: 0.4s;
  transform: scale(0.6);
}

.loader-message {
  margin: 0;
  color: #333;
  font-size: 16px;
  font-weight: 500;
  text-align: center;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

/* Transition animations */
.loader-fade-enter-active,
.loader-fade-leave-active {
  transition: opacity 0.2s ease;
}

.loader-fade-enter-from,
.loader-fade-leave-to {
  opacity: 0;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .modern-spinner {
    width: 50px;
    height: 50px;
  }

  .loader-message {
    font-size: 14px;
  }
}

/* Dark mode support */
@media (prefers-color-scheme: dark) {
  .modern-loader-overlay {
    background: rgba(0, 0, 0, 0.95);
  }

  .loader-message {
    color: #fff;
  }

  .spinner-ring:nth-child(1) {
    border-top-color: #0d6efd;
  }

  .spinner-ring:nth-child(2) {
    border-right-color: #0d6efd;
  }

  .spinner-ring:nth-child(3) {
    border-bottom-color: #0d6efd;
  }
}

/* Accessibility improvements */
@media (prefers-reduced-motion: reduce) {
  .spinner-ring {
    animation: none;
  }

  .modern-spinner::after {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 8px;
    height: 8px;
    background: #007bff;
    border-radius: 50%;
  }
}
</style>
